import React, { type FC, memo, useEffect, useState, useRef } from 'react';
import LoadingDots from '../tabs/LoadingDots';
import { useI18n } from '../../../../i18n';
import trackingKeys from '../../../../commons/constants/trackingKeys';
import S from './style/VerificationTabContent';
import GenericErrorPage from '../../../../commons/components/GenericErrorPage';
import SwitchToBusinessAccountModal from './SwitchToBusinessAccountModal';
import VerificationUpfrontModal from './VerificationUpfrontModal';

type EnvironmentUrl = string;

const { myDeliveries } = trackingKeys.touchPointPage;
const TRACKING_PREFIX = 'delivery_kyc_';

/**
 * Provides the domain name the KYC service is hosted at (based on the current environment)
 * @param currentEnvironment string
 */
const getKycServiceUrlByCurEnv = (currentEnvironment: string): EnvironmentUrl | null => {
  const kycServiceDomainsByEnv: Record<string, string> = {
    localhost: 'http://localhost:4000',
    staging: 'https://marketplace.pl.stg.eu.olx.org',
    production: 'https://marketplace.olx.pl',
    test: 'http://localhost:4000',
  };

  return kycServiceDomainsByEnv[currentEnvironment] || null;
};

// The page name returned by this function is used for tracking purposes.
const getKycStatusPageName = (kycStatusPageContent: string): string | null => {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(kycStatusPageContent, 'text/html');
    const cnt = doc.querySelector('#delivery-kyc-status-page');
    return cnt
      .getAttribute('data-kyc-status')
      ?.replace('PAGE_NAME_', TRACKING_PREFIX)
      .toLowerCase();
  } catch (err) {
    return null;
  }
};

//  fetches KYC status Page
const fetchKycStatusPageContent = (
  kycServiceUrl: EnvironmentUrl | null,
  isKYCUpfrontEnabled: boolean,
): Promise<string> => {
  if (kycServiceUrl == null) {
    return Promise.reject(new Error('No environment Url available.'));
  }

  const checkError = (resp: Response) =>
    resp.status >= 400 ? Promise.reject(new Error(`Response status: ${resp.status}`)) : resp;

  const renderWithoutHeader = 'skipKycStatusPageHeader=true';
  const analyticsQueryParams = 'access_source=delivery_kyc_verification_tab';
  const kycUpfront = isKYCUpfrontEnabled ? 'kycUpfront=true' : 'kycUpfront=false';

  return fetch(
    `${kycServiceUrl}/kyc/update?${renderWithoutHeader}&${analyticsQueryParams}&${kycUpfront}`,
    {
      method: 'GET',
      mode: 'cors',
      credentials: 'include',
    },
  )
    .then(checkError)
    .then(resp => resp.text());
};

const VerificationTabErrorPage = () => {
  const i18n = useI18n();

  return (
    <GenericErrorPage
      title={i18n`dlv_generic_error_page_title`}
      description={i18n`dlv_generic_error_page_description`}
      buttonTitle={i18n`dlv_errorpage_tohome`}
      buttonUrl="./"
    />
  );
};

const KYCStatusPage: React.FC<{
  kycStatusPageContent: string;
  onTrackPage: (pageName: string) => void;
  onTrackEvent: () => void;
}> = memo(
  ({
    kycStatusPageContent,
    onTrackPage = Function.prototype,
    onTrackEvent = Function.prototype,
  }) => {
    const trackEvent = onTrackEvent();

    const click = (e: React.SyntheticEvent): void => {
      const dataElement = (e.target as Element).getAttribute('data-element');
      if (!dataElement) {
        return;
      }
      if (['go_to_verification', 'go_to_my_ads', 'upfront'].includes(dataElement)) {
        trackEvent(`${TRACKING_PREFIX}${dataElement}`, 'click', null, {
          touch_point_page: `${getKycStatusPageName(kycStatusPageContent)}`,
        });
      }
    };

    onTrackPage(getKycStatusPageName(kycStatusPageContent) || 'verification_tab_no_page_detected', {
      touch_point_page: myDeliveries,
    });

    return (
      <S.StatusPageWrapper
        onClick={click}
        dangerouslySetInnerHTML={{ __html: kycStatusPageContent }}
        id="delivery-kyc-app-cnt"
      />
    );
  },
);

interface VerificationTabContentProps {
  currentEnvironment: string;
  onTrackEvent: () => void;
  onTrackPage: (pageName: string) => void;
  useFeatureFlag: (experimentId: string) => boolean;
}

/**
 * Renders the seller's current KYC status under the Verification tab
 */
const VerificationTabContent: FC<VerificationTabContentProps> = ({
  currentEnvironment,
  onTrackEvent,
  onTrackPage,
  useFeatureFlag,
}) => {
  const [isSwitchToBusinessAccountModalOpened, setSwitchToBusinessAccountModal] = useState(false);
  const [isVerificationUpfrontModalOpened, setVerificationUpfrontModal] = useState(false);
  const [kycStatusPageContent, setKycStatusPageContent] = useState<string>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);

  const isKYCUpfrontEnabled = Boolean(useFeatureFlag('mp-2532'));

  const handleOpenModal = (event: any) => {
    const dataElement = event?.target?.getAttribute('data-element');
    if (dataElement === 'migrate_to_business') {
      setSwitchToBusinessAccountModal(true);
    }

    if (dataElement === 'trigger_verification_upfront' || dataElement === 'upfront') {
      setVerificationUpfrontModal(true);
    }
  };

  const handleCloseModal = () => {
    setSwitchToBusinessAccountModal(false);
    setVerificationUpfrontModal(false);
  };
  const kycServiceUrl = getKycServiceUrlByCurEnv(currentEnvironment);
  const statusPageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchKycStatusPageContent(kycServiceUrl, isKYCUpfrontEnabled)
      .then(setKycStatusPageContent)
      .then(() => setIsLoading(false))
      .catch(() => {
        setHasError(true);
      });
  }, []);

  useEffect(() => {
    if (!isLoading || hasError) {
      statusPageRef?.current?.addEventListener('click', handleOpenModal);
    }

    return () => {
      statusPageRef?.current?.removeEventListener('click', handleOpenModal);
    };
  }, [isLoading, hasError]);

  if (hasError) {
    return <VerificationTabErrorPage />;
  }

  if (isLoading || !kycStatusPageContent) {
    return <LoadingDots />;
  }

  return (
    <S.VerificationTabContentWrapper data-test="VerificationTabContentWrapper" ref={statusPageRef}>
      <KYCStatusPage
        kycStatusPageContent={kycStatusPageContent}
        onTrackPage={onTrackPage}
        onTrackEvent={onTrackEvent}
      />

      {isSwitchToBusinessAccountModalOpened ? (
        <SwitchToBusinessAccountModal
          handleCloseModal={handleCloseModal}
          switchToBusinessAccountUrl={`${kycServiceUrl}/v1/kyc/payu/edit?switch_to_business=true`}
        />
      ) : null}

      {isVerificationUpfrontModalOpened ? (
        <VerificationUpfrontModal
          onTrackEvent={onTrackEvent}
          handleCloseModal={handleCloseModal}
          triggerVerificationUpfrontUrl={`${kycServiceUrl}/v1/kyc/payu/edit?trigger_verification_upfront=true`}
        />
      ) : null}
    </S.VerificationTabContentWrapper>
  );
};

export default VerificationTabContent;
